import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Plus,
  RefreshCw,
  Clock,
  Eye,
  Building,
  User,
  Mail,
  Phone,
  Calendar,
  Tag,
} from "lucide-react";
import {
  fetchCustomerRequests,
  clearError,
} from "../../features/customerRequests/customerRequestsSlice";
import PageLoader from "../../components/Common/PageLoader";
import { DataTable } from "../../components/Common/DataTable";
import RequestStats from "./components/RequestStats";
import CustomerRequestDetailView from "./components/CustomerRequestDetailView";
import CreateRequestModal from "./components/CreateRequestModal";
import { useClientFilter } from "../../hooks/useClientFilter";
import "./customer-requests.scss";

// Helper function to format date/time
function formatDateTime(dateString) {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

// Priority badge classes
function getPriorityClass(priority) {
  switch (priority) {
    case "Critical":
      return "badge badge--critical";
    case "High":
      return "badge badge--high";
    case "Medium":
      return "badge badge--medium";
    case "Low":
      return "badge badge--low";
    default:
      return "badge";
  }
}

// Status chip classes
function getStatusClass(status) {
  switch (status) {
    case "Open":
      return "chip chip--info";
    case "In Progress":
      return "chip chip--warn";
    case "Escalated":
      return "chip chip--danger";
    case "Completed":
      return "chip chip--success";
    case "On Hold":
      return "chip chip--muted";
    default:
      return "chip";
  }
}

export default function CustomerRequests() {
  const dispatch = useDispatch();
  const { requests, status, error } = useSelector(
    (state) => state.customerRequests
  );
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Get request ID from URL params
  const { requestId } = useParams();
  const navigate = useNavigate();

  // Use client filter hook for filtering by selected client
  const { filterByClient } = useClientFilter();

  // Filter requests by selected client
  const customerRequests = useMemo(() => {
    const allRequests = requests || [];
    return filterByClient(allRequests, 'clientUUID');
  }, [requests, filterByClient]);

  // Fetch customer requests on component mount
  useEffect(() => {
    dispatch(fetchCustomerRequests());
  }, [dispatch]);

  // Open request from URL parameter when requests are loaded
  useEffect(() => {
    if (requestId && customerRequests.length > 0) {
      // Find the request matching the URL parameter
      const requestFromUrl = customerRequests.find(
        (req) => String(req.id) === String(requestId)
      );
      if (requestFromUrl) {
        setSelectedRequest(requestFromUrl);
      }
    } else if (!requestId) {
      // URL no longer has request ID, close the modal
      setSelectedRequest(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, customerRequests]);

  // Handle selecting a request (updates URL)
  const handleSelectRequest = useCallback((request) => {
    setSelectedRequest(request);
    if (request) {
      navigate(`/customer-requests/${request.id}`, { replace: true });
    }
  }, [navigate]);

  // Handle closing request modal (clears URL)
  const handleCloseRequest = useCallback(() => {
    setSelectedRequest(null);
    navigate('/customer-requests', { replace: true });
  }, [navigate]);

  // Handle retry on error
  const handleRetry = () => {
    dispatch(clearError());
    dispatch(fetchCustomerRequests());
  };

  // Handle request update
  const handleRequestUpdate = () => {
    // Refresh the list after update
    dispatch(fetchCustomerRequests());
  };

  // Handle request delete
  const handleRequestDelete = () => {
    // Refresh the list after delete
    dispatch(fetchCustomerRequests());
  };

  // Handle create new request
  const handleCreateRequest = () => {
    // Refresh the list after create
    dispatch(fetchCustomerRequests());
  };

  // Calculate request counts for stats
  const requestCounts = useMemo(
    () => ({
      all: customerRequests.length,
      open: customerRequests.filter((r) => r.status === "Open").length,
      inProgress: customerRequests.filter((r) => r.status === "In Progress")
        .length,
      escalated: customerRequests.filter((r) => r.status === "Escalated")
        .length,
    }),
    [customerRequests]
  );

  // Handle row click
  const handleRowClick = (request) => {
    handleSelectRequest(request);
  };

  // Define table columns
  const columns = useMemo(
    () => [
      // ===== VISIBLE BY DEFAULT (6 columns) =====
      {
        accessorKey: "id",
        header: "ID",
        size: 100,
        cell: ({ getValue }) => (
          <span className="chip chip--muted font-mono">{getValue()}</span>
        ),
      },
      {
        accessorKey: "title",
        header: "Title",
        size: 280,
        cell: ({ getValue }) => {
          const title = getValue();
          return (
            <span className="request__title" title={title}>
              {title}
            </span>
          );
        },
      },
      {
        accessorKey: "customer",
        header: "Customer",
        size: 180,
        cell: ({ getValue }) => (
          <div className="inline">
            <Building size={12} className="icon-muted" />
            <span>{getValue()}</span>
          </div>
        ),
      },
      {
        accessorKey: "priority",
        header: "Priority",
        size: 100,
        cell: ({ getValue }) => {
          const priority = getValue();
          return <span className={getPriorityClass(priority)}>{priority}</span>;
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        cell: ({ getValue }) => {
          const status = getValue();
          return <span className={getStatusClass(status)}>{status}</span>;
        },
      },
      {
        accessorKey: "created",
        header: "Created",
        size: 160,
        cell: ({ getValue }) => (
          <div className="inline">
            <Clock size={12} className="icon-muted" />
            <span className="font-mono small">
              {formatDateTime(getValue())}
            </span>
          </div>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        size: 80,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) => (
          <button
            className="btn btn--outline icon"
            onClick={(e) => {
              e.stopPropagation();
              handleSelectRequest(row.original);
            }}
            title="View Details"
          >
            <Eye size={16} />
          </button>
        ),
      },

      // ===== HIDDEN BY DEFAULT (Additional Columns) =====
      {
        accessorKey: "description",
        header: "Description",
        size: 300,
        cell: ({ getValue }) => {
          const desc = getValue();
          const truncated =
            desc && desc.length > 80 ? desc.substring(0, 80) + "..." : desc;
          return (
            <span className="text-muted" title={desc}>
              {truncated || "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "contact",
        header: "Contact",
        size: 150,
        cell: ({ getValue }) => (
          <div className="inline">
            <User size={12} className="icon-muted" />
            <span>{getValue() || "-"}</span>
          </div>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 200,
        cell: ({ getValue }) => (
          <div className="inline">
            <Mail size={12} className="icon-muted" />
            <span className="font-mono small">{getValue() || "-"}</span>
          </div>
        ),
      },
      {
        accessorKey: "phone",
        header: "Phone",
        size: 140,
        cell: ({ getValue }) => (
          <div className="inline">
            <Phone size={12} className="icon-muted" />
            <span>{getValue() || "-"}</span>
          </div>
        ),
      },
      {
        accessorKey: "category",
        header: "Category",
        size: 140,
        cell: ({ getValue }) => (
          <span className="chip chip--purple">{getValue()}</span>
        ),
      },
      {
        accessorKey: "requestType",
        header: "Request Type",
        size: 160,
        cell: ({ getValue }) => (
          <span className="chip chip--green">{getValue()}</span>
        ),
      },
      {
        accessorKey: "dueDate",
        header: "Due Date",
        size: 160,
        cell: ({ getValue }) => (
          <div className="inline">
            <Calendar size={12} className="icon-muted" />
            <span className="font-mono small">
              {formatDateTime(getValue())}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "assignee",
        header: "Assignee",
        size: 150,
        cell: ({ getValue }) => (
          <div className="inline">
            <User size={12} className="icon-muted" />
            <span>{getValue() || "Unassigned"}</span>
          </div>
        ),
      },
      {
        accessorKey: "lastResponse",
        header: "Last Response",
        size: 160,
        cell: ({ getValue }) => (
          <div className="inline">
            <Clock size={12} className="icon-muted" />
            <span className="font-mono small">
              {formatDateTime(getValue())}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "tags",
        header: "Tags",
        size: 150,
        cell: ({ getValue }) => {
          const tags = getValue();
          return tags ? (
            <div className="inline">
              <Tag size={12} className="icon-muted" />
              <span className="chip chip--muted">{tags}</span>
            </div>
          ) : (
            <span className="text-muted">-</span>
          );
        },
      },
    ],
    [handleSelectRequest]
  );

  // Initial column visibility - hide additional columns by default
  const initialColumnVisibility = useMemo(
    () => ({
      description: false,
      contact: false,
      email: false,
      phone: false,
      category: false,
      requestType: false,
      dueDate: false,
      assignee: false,
      lastResponse: false,
      tags: false,
    }),
    []
  );

  return (
    <div className="customer-requests">
      {/* Page Header */}
      <div className="customer-requests__header">
        <div>
          <h1 className="customer-requests__title">Customer Requests</h1>
          <p className="customer-requests__subtitle">
            Manage customer security inquiries and requests
          </p>
          {/* Status Indicator */}
          <div className="api-status">
            {status === "loading" && (
              <span className="api-status__loading">🔄 Loading...</span>
            )}
            {error && (
              <span className="api-status__error">❌ Error: {error}</span>
            )}
          </div>
        </div>
        <div className="customer-requests__controls">
          <button
            className="btn btn--outline"
            onClick={handleRetry}
            disabled={status === "loading"}
          >
            <RefreshCw
              size={16}
              className={status === "loading" ? "spinning" : ""}
            />
            Refresh
          </button>
          <button
            className="btn btn--primary"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={16} />
            New Request
          </button>
        </div>
      </div>

      {/* Loading State */}
      {status === "loading" && <PageLoader />}

      {/* Stats Cards */}
      {status !== "loading" && <RequestStats requestCounts={requestCounts} />}

      {/* DataTable */}
      {status !== "loading" && (
        <DataTable
          data={customerRequests}
          columns={columns}
          onRowClick={handleRowClick}
          enableSearch={true}
          enableFilters={true}
          enableColumnVisibility={true}
          enableSorting={true}
          enablePagination={true}
          enableExport={true}
          enableDensity={true}
          pageSize={10}
          pageSizeOptions={[5, 10, 20, 50, 100]}
          emptyMessage="No customer requests found"
          loading={status === "loading"}
          initialColumnVisibility={initialColumnVisibility}
        />
      )}

      {/* Request Detail Modal */}
      {selectedRequest && (
        <CustomerRequestDetailView
          request={selectedRequest}
          onClose={handleCloseRequest}
          onUpdate={handleRequestUpdate}
          onDelete={handleRequestDelete}
        />
      )}

      {/* Create Request Modal */}
      {showCreateModal && (
        <CreateRequestModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateRequest}
        />
      )}
    </div>
  );
}
